import { Primitive } from '@radix-ui/react-primitive';
import { type ComponentPropsWithoutRef, memo, useCallback, useState } from 'react';
import { type DropTargetMonitor, useDrop } from 'react-dnd';

import { type Board } from '@air/api/types';
import { cn } from '@air/utils-tailwind';

import { type DroppableBoardInterface } from '~/components/Shared/Drag/DNDParentBoard/methods';
import {
  boardDragTypes,
  DragType,
  type DropData,
  type DropLocation,
  isBoardDragType,
  isGetBoardDragItem,
  type RearrangableItemType,
} from '~/components/Shared/Drag/dragTypes';
import { useBoardPermissionsCache } from '~/hooks/useBoardPermissionsCache';
import { selectedBoardsSelector } from '~/store/selectedItems/selectors';
import { canAddAssetsToBoard, canCreateBoard, canMoveAssetsToBoard } from '~/utils/permissions/boardPermissions';
import { useAirStore } from '~/utils/ReduxUtils';

const canMoveBoardsToBoard = (
  boardsToMove: Pick<Board, 'id' | 'parentId'>[],
  { id: parentId, ancestors: parentAncestors = [] }: DroppableBoardInterface,
): boolean =>
  boardsToMove.every(
    (boardToMove) =>
      !!boardToMove &&
      !!boardToMove.id &&
      boardToMove.id !== parentId &&
      !parentAncestors.some((a) => a.id === boardToMove.id),
  );

export type DNDBoardDropProps = ComponentPropsWithoutRef<typeof Primitive.div> & {
  board: Pick<Board, 'id' | 'ancestors' | 'title'>;
  dropLocation: DropLocation;
  enabled?: boolean;
  supportedBoardDragTypes?: DragType[];
};

export const DNDBoardDrop = memo(
  ({
    board,
    className,
    dropLocation,
    enabled = true,
    supportedBoardDragTypes = boardDragTypes,
    ...restOfProps
  }: DNDBoardDropProps) => {
    const [canDrop, setCanDrop] = useState(false);
    const store = useAirStore();
    const selectedBoards = selectedBoardsSelector(store.getState());
    const { getBoardPermissions } = useBoardPermissionsCache();

    const checkIfCanDrop = useCallback(
      (monitor: DropTargetMonitor<RearrangableItemType>) => {
        // this comes from the `getItem` of DNDMovableToBoardItem
        const item = monitor.getItem();

        const permissions = getBoardPermissions(board.id);

        // this function determines whether or not the dragged item can be dropped here
        // we only want to check if there are selected boards or the user has physically dragged a board
        if ((isBoardDragType(item.type) || selectedBoards.length) && canCreateBoard(permissions)) {
          const boardsToCheck =
            isGetBoardDragItem(item) && item.firstItem ? [...selectedBoards, item.firstItem] : selectedBoards;

          return canMoveBoardsToBoard(boardsToCheck, board) && enabled;
        } else {
          return (canAddAssetsToBoard(permissions) || canMoveAssetsToBoard(permissions)) && enabled;
        }
      },
      [board, enabled, getBoardPermissions, selectedBoards],
    );

    const [{ isOver }, drop] = useDrop({
      accept: [...supportedBoardDragTypes, DragType.asset, DragType.file],
      drop: (): DropData => ({
        type: 'move',
        location: dropLocation,
        data: board,
      }),
      hover: (_props, monitor) => {
        const canDropItem = checkIfCanDrop(monitor);

        if (canDropItem !== canDrop) {
          setCanDrop(canDropItem);
        }
      },
      collect: (monitor) => ({
        isOver: monitor.isOver(),
      }),
      canDrop: (_props, monitor) => checkIfCanDrop(monitor),
    });

    return (
      <Primitive.div
        data-dnd-droppable={canDrop}
        data-dnd-over={isOver}
        data-draggable="true"
        className={cn('group/dnd-board relative', className)}
        {...restOfProps}
        ref={drop}
      />
    );
  },
);

DNDBoardDrop.displayName = 'DNDBoardDrop';
